<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Visitor Types</h5>
        <p class="page-sub">Define categories of visitors with different check-in rules and badge colours</p>
      </div>
      <button class="btn-primary-action" @click="openCreate">
        <font-awesome-icon icon="plus" /> Add Visitor Type
      </button>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <div v-else-if="!types.length" class="empty-state">
      <font-awesome-icon icon="id-badge" class="empty-icon" />
      <p class="empty-title">No visitor types yet</p>
      <p class="empty-sub">Create types like "Contractor", "VIP", or "Delivery" to apply different check-in rules.</p>
      <button class="btn-primary-action" @click="openCreate">Add First Type</button>
    </div>

    <div v-else class="types-grid">
      <div v-for="t in types" :key="t.id" class="type-card">
        <div class="type-band" :style="{ background: t.colour }"></div>
        <div class="type-body">
          <div class="type-name-row">
            <span class="type-name">{{ t.name }}</span>
            <span class="type-badge" :class="t.active ? 'badge--active' : 'badge--inactive'">{{ t.active ? 'Active' : 'Inactive' }}</span>
          </div>
          <p v-if="t.description" class="type-desc">{{ t.description }}</p>
          <div class="type-rules">
            <span v-if="t.require_approval"       class="rule-chip rule-chip--amber"><font-awesome-icon icon="check-circle" /> Requires Approval</span>
            <span v-if="t.require_nda"            class="rule-chip rule-chip--blue"><font-awesome-icon icon="file-signature" /> NDA Required</span>
            <span v-if="t.require_photo"          class="rule-chip rule-chip--blue"><font-awesome-icon icon="camera" /> Photo Required</span>
            <span v-if="t.require_induction"      class="rule-chip rule-chip--purple"><font-awesome-icon icon="play-circle" /> Induction</span>
            <span v-if="t.require_escort"         class="rule-chip rule-chip--red"><font-awesome-icon icon="user-shield" /> Escort Required</span>
            <span v-if="t.requires_permit"        class="rule-chip rule-chip--orange"><font-awesome-icon icon="hard-hat" /> Permit Required</span>
            <span v-if="t.requires_locker"        class="rule-chip rule-chip--teal"><font-awesome-icon icon="box" /> Locker Required</span>
            <span v-if="t.require_pre_registration" class="rule-chip rule-chip--green"><font-awesome-icon icon="calendar-check" /> Pre-Reg Only</span>
            <span v-if="t.max_duration_hours"     class="rule-chip rule-chip--grey"><font-awesome-icon icon="clock" /> Max {{ t.max_duration_hours }}h</span>
          </div>
          <div class="type-meta">
            <span>Retention: {{ t.retention_days }} days</span>
            <span v-if="t.allowed_hours">Hours: {{ t.allowed_hours.from }}–{{ t.allowed_hours.to }}</span>
          </div>
        </div>
        <div class="type-actions">
          <button class="btn-icon" @click="openEdit(t)" title="Edit"><font-awesome-icon icon="pencil-alt" /></button>
          <button class="btn-icon btn-icon--red" @click="confirmDelete(t)" title="Delete"><font-awesome-icon icon="trash" /></button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="modal" class="modal-backdrop" @mousedown.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6 class="modal-title">{{ editing ? 'Edit Visitor Type' : 'New Visitor Type' }}</h6>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input v-model="form.name" class="form-input" maxlength="100" placeholder="e.g. Contractor" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Description</label>
            <input v-model="form.description" class="form-input" maxlength="250" placeholder="Brief description of this visitor type" />
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Badge Colour Band</label>
            <div style="display:flex;align-items:center;gap:10px;">
              <input v-model="form.colour" type="color" class="colour-picker" />
              <input v-model="form.colour" class="form-input" style="max-width:110px;" placeholder="#6366f1" />
            </div>
          </div>

          <hr class="section-divider" />
          <div class="section-label">Check-In Rules</div>

          <div class="toggle-grid">
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Requires Pre-Registration</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_pre_registration" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Requires Approval</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_approval" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Requires NDA</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_nda" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Requires Photo</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_photo" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Requires Induction</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_induction" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info"><span class="toggle-name">Escort Required</span></div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.require_escort" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-name">Requires Permit to Work</span>
                <span class="toggle-hint">Visitors of this type must provide a permit number when checking in</span>
              </div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.requires_permit" /><span class="toggle-track"></span></label>
            </label>
            <label class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-name">Requires Locker</span>
                <span class="toggle-hint">Automatically assign an available locker on check-in</span>
              </div>
              <label class="toggle-switch"><input type="checkbox" v-model="form.requires_locker" /><span class="toggle-track"></span></label>
            </label>
          </div>

          <div class="two-col" style="margin-top:14px;">
            <div class="form-row">
              <label class="form-label">Max Duration (hours)</label>
              <input v-model.number="form.max_duration_hours" type="number" min="1" max="24" class="form-input" placeholder="No limit" />
            </div>
            <div class="form-row">
              <label class="form-label">Data Retention (days)</label>
              <input v-model.number="form.retention_days" type="number" min="1" max="3650" class="form-input" />
            </div>
          </div>

          <div class="two-col" style="margin-top:14px;">
            <div class="form-row">
              <label class="form-label">Allowed From</label>
              <input v-model="form.allowed_hours_from" type="time" class="form-input" placeholder="08:00" />
            </div>
            <div class="form-row">
              <label class="form-label">Allowed To</label>
              <input v-model="form.allowed_hours_to" type="time" class="form-input" placeholder="18:00" />
            </div>
          </div>

          <div class="toggle-row" style="margin-top:14px;">
            <div class="toggle-info"><span class="toggle-name">Active</span></div>
            <label class="toggle-switch"><input type="checkbox" v-model="form.active" /><span class="toggle-track"></span></label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeModal">Cancel</button>
          <button class="btn-primary-action" @click="save" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Type') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="modal-backdrop" @mousedown.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Delete Visitor Type?</h6>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;">Are you sure you want to delete <strong>{{ deleteTarget.name }}</strong>? This cannot be undone. If this type is assigned to visits, the deletion will be blocked.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="deleting">
            <font-awesome-icon v-if="deleting" icon="spinner" spin />
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorTypes',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true,
      types: [],
      modal: false,
      editing: null,
      saving: false,
      deleteTarget: null,
      deleting: false,
      form: this.blankForm(),
    }
  },
  created() { this.load() },
  methods: {
    blankForm() {
      return {
        name: '', description: '', colour: '#6366f1',
        require_pre_registration: false, require_approval: false,
        require_nda: false, require_photo: false,
        require_induction: false, require_escort: false, requires_permit: false, requires_locker: false,
        max_duration_hours: null, retention_days: 90, active: true,
        allowed_hours_from: '', allowed_hours_to: '',
      }
    },
    async load() {
      this.loading = true
      try {
        const r = await axios.get('visitors/types')
        this.types = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load visitor types.') }
      finally { this.loading = false }
    },
    openCreate() { this.form = this.blankForm(); this.editing = null; this.modal = true },
    openEdit(t) {
      this.editing = t
      this.form = {
        name: t.name, description: t.description ?? '', colour: t.colour ?? '#6366f1',
        require_pre_registration: t.require_pre_registration,
        require_approval: t.require_approval,
        require_nda: t.require_nda, require_photo: t.require_photo,
        require_induction: t.require_induction, require_escort: t.require_escort,
        requires_permit:      t.requires_permit ?? false,
        requires_locker:      t.requires_locker ?? false,
        max_duration_hours:   t.max_duration_hours ?? null,
        retention_days:       t.retention_days ?? 90,
        active:               t.active,
        allowed_hours_from: t.allowed_hours?.from ?? '',
        allowed_hours_to:   t.allowed_hours?.to   ?? '',
      }
      this.modal = true
    },
    closeModal() { this.modal = false; this.editing = null },
    async save() {
      if (!this.form.name.trim()) { this.toast.error('Name is required.'); return }
      this.saving = true
      const payload = {
        ...this.form,
        allowed_hours: this.form.allowed_hours_from && this.form.allowed_hours_to
          ? { from: this.form.allowed_hours_from, to: this.form.allowed_hours_to }
          : null,
      }
      delete payload.allowed_hours_from
      delete payload.allowed_hours_to
      try {
        if (this.editing) {
          await axios.put(`visitors/types/${this.editing.id}`, payload)
          this.toast.success('Visitor type updated.')
        } else {
          await axios.post('visitors/types', payload)
          this.toast.success('Visitor type created.')
        }
        this.closeModal()
        await this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    confirmDelete(t) { this.deleteTarget = t },
    async doDelete() {
      this.deleting = true
      try {
        await axios.delete(`visitors/types/${this.deleteTarget.id}`)
        this.toast.success('Visitor type deleted.')
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to delete.')
      } finally { this.deleting = false }
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#e53e3e; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.types-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:16px; }
.type-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; display:flex; flex-direction:column; }
.type-band  { height:6px; flex-shrink:0; }
.type-body  { padding:16px; flex:1; }
.type-name-row { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.type-name  { font-size:15px; font-weight:700; color:#1a2b45; }
.type-desc  { font-size:12px; color:#7a90b0; margin:0 0 10px; }
.type-rules { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:10px; }
.type-meta  { display:flex; gap:14px; font-size:11px; color:#aab8cc; }
.type-actions { display:flex; gap:8px; padding:10px 16px; border-top:1px solid #f0f4f8; background:#fafbfd; }

.badge--active   { background:#dcfce7; color:#16a34a; }
.badge--inactive { background:#fee2e2; color:#dc2626; }
.badge--active, .badge--inactive { font-size:11px; font-weight:700; padding:2px 8px; border-radius:20px; }

.rule-chip { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600; padding:3px 8px; border-radius:20px; }
.rule-chip--amber  { background:#fef3c7; color:#d97706; }
.rule-chip--blue   { background:#dbeafe; color:#1d4ed8; }
.rule-chip--purple { background:#ede9fe; color:#7c3aed; }
.rule-chip--red    { background:#fee2e2; color:#dc2626; }
.rule-chip--orange { background:#fff7ed; color:#c2410c; }
.rule-chip--teal   { background:#ccfbf1; color:#0f766e; }
.rule-chip--green  { background:#dcfce7; color:#16a34a; }
.rule-chip--grey   { background:#f1f5f9; color:#64748b; }

.btn-icon { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#4a5e78; cursor:pointer; font-size:13px; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--red { color:#dc2626; }
.btn-icon--red:hover { background:#fee2e2; border-color:#fca5a5; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0 0 20px; max-width:440px; margin-left:auto; margin-right:auto; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; flex:1; overflow-y:auto; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#e53e3e; }
.two-col { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.colour-picker { width:42px; height:38px; border:1px solid #dde5f0; border-radius:8px; padding:2px; cursor:pointer; }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:10px; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:18px 0; }

.toggle-grid { display:flex; flex-direction:column; gap:10px; }
.toggle-row  { display:flex; justify-content:space-between; align-items:center; gap:16px; padding:4px 0; }
.toggle-info { display:flex; flex-direction:column; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint { font-size:11px; color:#7a90b0; margin-top:2px; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
